import type { Connection } from "@xyflow/react";
import type {
  ExpressionNode,
  ExpressionEdge,
  ConnectionValidationResult,
  GraphValidationResult,
  ExpressionNodeType,
} from "@/types/decision-graph";
import { HANDLE_TYPES } from "@/types/decision-graph";

export function validateConnection(
  connection: Connection,
  nodes: ExpressionNode[],
  edges: ExpressionEdge[]
): ConnectionValidationResult {
  const { source, target, sourceHandle, targetHandle } = connection;

  if (!source || !target) {
    return { valid: false, reason: "Missing source or target" };
  }

  if (source === target) {
    return { valid: false, reason: "Cannot connect node to itself" };
  }

  const existingEdge = edges.find(
    (e) => e.target === target && e.targetHandle === targetHandle
  );
  if (existingEdge) {
    return { valid: false, reason: "Target handle already connected" };
  }

  const sourceNode = nodes.find((n) => n.id === source);
  const targetNode = nodes.find((n) => n.id === target);

  if (!sourceNode || !targetNode) {
    return { valid: false, reason: "Node not found" };
  }

  const sourceNodeType = sourceNode.type as ExpressionNodeType;
  const targetNodeType = targetNode.type as ExpressionNodeType;

  const sourceHandleTypes = HANDLE_TYPES[sourceNodeType];
  const targetHandleTypes = HANDLE_TYPES[targetNodeType];

  if (!sourceHandleTypes || !targetHandleTypes) {
    return { valid: true };
  }

  const sourceType = sourceHandleTypes[sourceHandle || "result"];
  const targetType = targetHandleTypes[targetHandle || "input"];

  if (!sourceType || !targetType) {
    return { valid: true };
  }

  if (targetType !== "any" && sourceType !== targetType) {
    return {
      valid: false,
      reason: `Type mismatch: ${sourceType} cannot connect to ${targetType}`,
    };
  }

  return { valid: true };
}

export function wouldCreateCycle(
  connection: Connection,
  edges: ExpressionEdge[]
): boolean {
  const { source, target } = connection;
  if (!source || !target) return false;

  const visited = new Set<string>();
  const queue = [target];

  while (queue.length > 0) {
    const current = queue.shift()!;

    if (current === source) {
      return true;
    }

    if (visited.has(current)) {
      continue;
    }
    visited.add(current);

    const outgoing = edges.filter((e) => e.source === current);
    for (const edge of outgoing) {
      queue.push(edge.target);
    }
  }

  return false;
}

export function validateGraph(
  nodes: ExpressionNode[],
  edges: ExpressionEdge[]
): GraphValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  const outputNodes = nodes.filter((n) => n.data.nodeType === "output");
  if (outputNodes.length === 0) {
    errors.push("Graph must have an output node");
  } else if (outputNodes.length > 1) {
    errors.push("Graph can only have one output node");
  }

  if (outputNodes.length === 1) {
    const hasInput = edges.some((e) => e.target === outputNodes[0].id);
    if (!hasInput) {
      errors.push("Output node must have an input connection");
    }
  }

  for (const node of nodes) {
    const nodeType = node.data.nodeType;
    const incoming = edges.filter((e) => e.target === node.id);

    switch (nodeType) {
      case "binary":
      case "comparison": {
        const hasLeft = incoming.some((e) => e.targetHandle === "left");
        const hasRight = incoming.some((e) => e.targetHandle === "right");
        if (!hasLeft || !hasRight) {
          errors.push(
            `${node.data.label} node is missing input connections`
          );
        }
        break;
      }

      case "unary":
      case "existence": {
        const hasOperand = incoming.some(
          (e) => e.targetHandle === "operand"
        );
        if (!hasOperand) {
          errors.push(
            `${node.data.label} node is missing operand connection`
          );
        }
        break;
      }

      case "membership": {
        const hasOperand = incoming.some(
          (e) => e.targetHandle === "operand"
        );
        if (!hasOperand) {
          errors.push(
            `${node.data.label} node is missing operand connection`
          );
        }
        const hasSetElements = incoming.some((e) =>
          e.targetHandle?.startsWith("set-")
        );
        if (
          !hasSetElements &&
          "setSize" in node.data &&
          (node.data as { setSize: number }).setSize > 0
        ) {
          warnings.push(
            `${node.data.label} node has no set elements connected`
          );
        }
        break;
      }

      case "function": {
        const connectedArgs = incoming.filter((e) =>
          e.targetHandle?.startsWith("arg-")
        ).length;
        if (
          "argumentCount" in node.data &&
          connectedArgs <
            (node.data as { argumentCount: number }).argumentCount
        ) {
          warnings.push(
            `${node.data.label} function expects ${(node.data as { argumentCount: number }).argumentCount} arguments but only ${connectedArgs} connected`
          );
        }
        break;
      }
    }
  }

  const connectedNodeIds = new Set<string>();
  for (const edge of edges) {
    connectedNodeIds.add(edge.source);
    connectedNodeIds.add(edge.target);
  }

  for (const node of nodes) {
    if (!connectedNodeIds.has(node.id)) {
      if (
        node.data.nodeType !== "literal" &&
        node.data.nodeType !== "field"
      ) {
        warnings.push(`${node.data.label} node is disconnected`);
      }
    }
  }

  if (hasCycle(nodes, edges)) {
    errors.push("Graph contains a cycle");
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

function hasCycle(
  nodes: ExpressionNode[],
  edges: ExpressionEdge[]
): boolean {
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  const adjacency = new Map<string, string[]>();
  for (const node of nodes) {
    adjacency.set(node.id, []);
  }
  for (const edge of edges) {
    const targets = adjacency.get(edge.source) || [];
    targets.push(edge.target);
    adjacency.set(edge.source, targets);
  }

  function dfs(nodeId: string): boolean {
    visited.add(nodeId);
    recursionStack.add(nodeId);

    const neighbors = adjacency.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        if (dfs(neighbor)) return true;
      } else if (recursionStack.has(neighbor)) {
        return true;
      }
    }

    recursionStack.delete(nodeId);
    return false;
  }

  for (const node of nodes) {
    if (!visited.has(node.id)) {
      if (dfs(node.id)) return true;
    }
  }

  return false;
}

export function getConnectedToOutput(
  nodes: ExpressionNode[],
  edges: ExpressionEdge[]
): Set<string> {
  const connected = new Set<string>();

  const outputNode = nodes.find((n) => n.data.nodeType === "output");
  if (!outputNode) return connected;

  const queue = [outputNode.id];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (connected.has(current)) continue;

    connected.add(current);

    const incoming = edges.filter((e) => e.target === current);
    for (const edge of incoming) {
      queue.push(edge.source);
    }
  }

  return connected;
}
